import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { pushCtaClickEventToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables.js';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  billingAccountsMsg,
  connectionProblemsMsg,
  invoicesMsg,
  omatEdutMsg,
  pukCodeMsg,
  t,
  voiceSubscriptionsMsg,
} from '../../common/i18n';
import { getActiveAccountMasterId } from '../../selfservice/common/localStorageUtils';
import { SelectNewSubscriptionDialog } from '../Dialogs/SelectNewSubscriptionDialog';
import { ElisaIdMigrationCard } from '../ElisaIdMigration/ElisaIdMigrationCard';
import { LinkListModal } from '../Modal/LinkListModal';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy';
import type { BillingAccountHeader } from '../../generated/api/billingAccountHeader';
import type { InvoiceHeader } from '../../generated/api/invoiceHeader';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';

import './SelfServiceHomeFavoritesAndOrderNew.scss';

export interface SelfServiceHomeFavoritesAndOrderNewProps {
  voiceSubscriptions: SubscriptionHeader[] | undefined;
  invoices: InvoiceHeader[] | undefined;
  billingAccounts: BillingAccountHeader[] | undefined;
}

export const MODAL_TYPES = {
  puk: 'puk',
  invoices: 'invoices',
  billingAccounts: 'billingAccounts',
  connectionProblems: 'connectionProblems',
};

export type ModalType = keyof typeof MODAL_TYPES;

const dialogsConfig = {
  voice: {
    hideBroadband: true,
    hideVoice: false,
    hideBlankSim: false,
  },
  broadband: {
    hideBroadband: false,
    hideVoice: true,
    hideBlankSim: true,
  },
};

type ModalData = SubscriptionHeader[] | InvoiceHeader[] | BillingAccountHeader[] | undefined;

const modalsConfig: Record<
  ModalType,
  {
    label: string;
    route: string;
    icon: string;
    getData: (props: SelfServiceHomeFavoritesAndOrderNewProps) => ModalData;
    showAll?: boolean;
  }
> = {
  puk: {
    label: t.AR0B(voiceSubscriptionsMsg),
    route: paths.PS_MOBILE_SUBSCRIPTIONS,
    icon: 'sim',
    getData: props => props.voiceSubscriptions,
  },
  invoices: {
    label: t.Y7C0(invoicesMsg),
    route: paths.INVOICES,
    icon: 'invoice',
    getData: props => props.invoices,
    showAll: true,
  },
  billingAccounts: {
    label: t.ZVMK(billingAccountsMsg),
    route: paths.BILLING_ACCOUNTS,
    icon: 'draft',
    getData: props => props.billingAccounts,
  },
  connectionProblems: {
    label: t.AR0B(voiceSubscriptionsMsg),
    route: paths.PS_MOBILE_SUBSCRIPTIONS,
    icon: 'sim',
    getData: props => props.voiceSubscriptions,
  },
};

const FavoritesSection = ({
  setActiveModal,
  props,
}: {
  setActiveModal: (modal: ModalType | null) => void;
  props: SelfServiceHomeFavoritesAndOrderNewProps;
}) => {
  const handleFavoriteClick = (favoriteTitle: string, favoriteUrl?: string) => {
    pushCtaClickEventToDataLayer('NOE Favorites section', favoriteTitle, favoriteUrl);
  };

  const favoritesConfig = [
    {
      key: MODAL_TYPES.puk,
      icon: 'puk',
      title: t.K5TL(pukCodeMsg),
      path: paths.PS_MOBILE_SUBSCRIPTIONS,
      text: t.NET2('Check PUK codes in the subscription details'),
      modalKey: MODAL_TYPES.puk,
    },
    {
      key: MODAL_TYPES.invoices,
      icon: 'invoice',
      title: t.RMBH('Invoice copies'),
      path: paths.INVOICES,
      text: t.OFYZ('Download PDF copies of invoices'),
      modalKey: MODAL_TYPES.invoices,
    },
    {
      key: MODAL_TYPES.billingAccounts,
      icon: 'file-general',
      title: t.ZVMK(billingAccountsMsg),
      path: paths.BILLING_ACCOUNTS,
      text: t.C0OX('Edit billing account details'),
      modalKey: MODAL_TYPES.billingAccounts,
    },
    {
      key: 'barringServices',
      icon: 'block',
      title: t.DRLC('Barring services'),
      path: paths.PS_MOBILE_SUBSCRIPTIONS,
      text: t.ZBTH('Check and edit barring services in the subscription details'),
    },
    {
      key: 'supportMessage',
      icon: 'new-message',
      title: t.RLXT('Create support case or message'),
      path: paths.MESSAGE_CENTER_SUPPORT_CASE_NEW,
      text: t.NRRT('Send a message to our customer service'),
    },
    {
      key: MODAL_TYPES.connectionProblems,
      icon: 'help',
      title: t.OTF5(connectionProblemsMsg),
      path: paths.PS_MOBILE_SUBSCRIPTIONS,
      text: t.D9LX('Troubleshoot issues in the subscription details'),
      modalKey: MODAL_TYPES.connectionProblems,
    },
  ];

  return (
    <div
      className="of-self-service-home__favorites-and-order-new__favorites"
      // eslint-disable-next-line react/no-unknown-property
      analytics-parent-element="NOE Favorites section"
    >
      <h3>{t.EGPN('Favorites')}</h3>
      <div className="of-self-service-home__favorites-and-order-new__favorites__content">
        {favoritesConfig.map(favorite => {
          if (favorite.modalKey) {
            const modalConf = modalsConfig[favorite.modalKey as ModalType];
            const data = modalConf.getData(props);

            if (favorite.modalKey !== MODAL_TYPES.invoices && (!data || data.length === 0 || data.length > 8)) {
              return (
                <CL.LinkBlock
                  key={favorite.key}
                  href={favorite.path}
                  border="top"
                  icon={<CL.Icon icon={favorite.icon} />}
                  onClick={() => handleFavoriteClick(favorite.title, favorite.path)}
                >
                  <h4>{favorite.title}</h4>
                  <p>{favorite.text}</p>
                </CL.LinkBlock>
              );
            }

            if (data && data.length > 0) {
              return (
                <CL.LinkBlock
                  key={favorite.key}
                  border="top"
                  icon={<CL.Icon icon={favorite.icon} />}
                  onClick={() => setActiveModal(favorite.modalKey as ModalType)}
                >
                  <h4>{favorite.title}</h4>
                  <p>{favorite.text}</p>
                </CL.LinkBlock>
              );
            }
          }

          return (
            <CL.LinkBlock
              key={favorite.key}
              href={favorite.path}
              border="top"
              icon={<CL.Icon icon={favorite.icon} />}
              onClick={() => handleFavoriteClick(favorite.title, favorite.path)}
            >
              <h4>{favorite.title}</h4>
              <p>{favorite.text}</p>
            </CL.LinkBlock>
          );
        })}
      </div>
    </div>
  );
};

const OrderNewSection = ({ setActiveDialog }: { setActiveDialog: (dialog: 'voice' | 'broadband') => void }) => {
  const orderNewConfig = [
    {
      key: 'voiceSubscription',
      icon: 'smartphone',
      label: t.HSM8('Order voice subscription'),
      action: () => setActiveDialog('voice'),
    },
    {
      key: 'device',
      icon: 'devices',
      label: t.KD5A('Order device'),
      href: paths.WEB_SHOP,
      target: '_blank',
    },
    {
      key: 'broadband',
      icon: 'broadband',
      label: t.NLGJ('Order broadband'),
      action: () => setActiveDialog('broadband'),
    },
    {
      key: 'simCard',
      icon: 'sim',
      label: t.TE0F('Order SIM-card'),
      action: () => setActiveDialog('voice'),
    },
    {
      key: 'myBenefits',
      icon: 'diamond',
      iconType: 'light',
      label: t.SLPO(omatEdutMsg),
      href: paths.MY_OFFERS,
    },
  ];

  return (
    <div
      className="of-self-service-home__favorites-and-order-new__order-new"
      // eslint-disable-next-line react/no-unknown-property
      analytics-parent-element="NOE Order new section"
    >
      <div className="of-self-service-home__favorites-and-order-new__order-new__content">
        <h3>{t.BJVQ('Order new')}</h3>

        <div className="of-self-service-home__favorites-and-order-new__order-new__content__links">
          {orderNewConfig.map(item => {
            const linkBlockProps: CL.LinkBlockProps & { href?: string } = {
              border: 'top',
              icon: <CL.Icon icon={item.icon} type={item.iconType as 'light' | 'regular' | 'filled' | undefined} />,
              children: item.label,
              href: item.href,
              target: item.target,
              onClick: item.action,
            };
            return <CL.LinkBlock key={item.key} {...linkBlockProps} />;
          })}
        </div>
      </div>

      <img src={`${pictureStorageBaseUrl}7qv9OT4jEqdpHuHlxXv6iq/order-new.svg?w=800`} alt="Order new illustration" />
    </div>
  );
};

export const SelfServiceHomeFavoritesAndOrderNew = (props: SelfServiceHomeFavoritesAndOrderNewProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [activeDialog, setActiveDialog] = useState<'voice' | 'broadband' | null>(null);

  const modalConf = activeModal ? modalsConfig[activeModal] : undefined;
  const modalData = activeModal && modalConf ? modalConf.getData(props) || [] : [];

  return (
    <div className="of-self-service-home__favorites-and-order-new">
      <ElisaIdMigrationCard />
      <FavoritesSection setActiveModal={setActiveModal} props={props} />
      <OrderNewSection setActiveDialog={setActiveDialog} />

      {activeModal && modalConf && (
        <LinkListModal
          heading={modalConf.label}
          data={modalData.slice(0, 8)}
          modalType={activeModal}
          icon={modalConf.icon}
          onClose={() => setActiveModal(null)}
          {...(activeModal === MODAL_TYPES.invoices ? { showAllLinkRoute: modalConf.route } : {})}
        />
      )}

      {activeDialog && dialogsConfig[activeDialog] && (
        <SelectNewSubscriptionDialog
          onCloseDialog={() => setActiveDialog(null)}
          {...dialogsConfig[activeDialog]}
          mdmId={mdmId || getActiveAccountMasterId()}
          clickElementParent="NOE Order new section"
        />
      )}
    </div>
  );
};
